
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaShoppingCart, FaHospital, FaAppleAlt, FaBookMedical, FaStar,
  FaCheckCircle, FaShieldAlt, FaCreditCard, FaUserMd, FaArrowLeft,
  FaArrowRight, FaMapMarkerAlt, FaPhoneAlt, FaClock, FaHeart,
  FaTruck, FaLeaf, FaBrain, FaMobileAlt, FaBolt, FaRocket,
  FaTimes, FaUpload, FaQrcode, FaCheck, FaSpinner, FaShoppingBag, FaTimesCircle
} from 'react-icons/fa';
import {
  GiMedicinePills, GiStethoscope, GiHeartBeats, GiHealthPotion,
  GiDoctorFace, GiMedicalPackAlt, GiFruitBowl, GiHealthNormal
} from 'react-icons/gi';
import { motion, AnimatePresence } from 'framer-motion';
import { IoMdFitness } from 'react-icons/io';
import { database, storage } from '../firebase';
import { ref as dbRef, onValue, get, set, push } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import anusayaNewImg from '../asstes/anusayanew.png';

const HomePage = () => {
  const navigate = useNavigate();
  // State for slider
  const [slides, setSlides] = useState([]);
  const [loadingSliders, setLoadingSliders] = useState(true);
  const [sliderImagesReady, setSliderImagesReady] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const observerRef = useRef();

  // State for dynamic data from Firebase
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [hospitalData, setHospitalData] = useState(null);
  const [dietPlans, setDietPlans] = useState([]);
  const [ebooks, setEbooks] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingHospital, setLoadingHospital] = useState(true);
  const [loadingDietPlans, setLoadingDietPlans] = useState(true);
  const [loadingEbooks, setLoadingEbooks] = useState(true);
  const [cart, setCart] = useState([]);
  
  // Payment checkout states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentStep, setPaymentStep] = useState(1); // 1: Delivery, 2: Payment, 3: Confirmation
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [paymentScreenshotPreview, setPaymentScreenshotPreview] = useState("");
  const [upiId, setUpiId] = useState("");
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [paymentQRCodes, setPaymentQRCodes] = useState({ googlePay: '', phonePe: '' });
  const [qrLoading, setQrLoading] = useState(true);
  
  // Selected product for Buy Now
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);

  // Default gradient colors for sliders
  const defaultGradients = [
    "from-blue-600 via-teal-500 to-emerald-500",
    "from-emerald-600 via-green-500 to-teal-400",
    "from-purple-600 via-pink-500 to-rose-400",
    "from-orange-600 via-red-500 to-pink-400",
    "from-indigo-600 via-purple-500 to-pink-400"
  ];

  // Fetch sliders from Firebase
  useEffect(() => {
    const slidersRef = dbRef(database, 'Dr/homepage/sliders');
    const fallbackRef = dbRef(database, 'Dr/homepage/slider');
    
    // First try the new structure (Dr/homepage/sliders)
    const unsubscribe = onValue(slidersRef, (snapshot) => {
      let foundValidData = false;
      
      if (snapshot.exists()) {
        const slidersData = snapshot.val();
        let slidersArray = [];

        if (Array.isArray(slidersData)) {
          slidersArray = slidersData;
        } else if (typeof slidersData === 'object') {
          slidersArray = Object.values(slidersData).filter(item => item && typeof item === 'object');
        }

        if (slidersArray.length > 0) {
          foundValidData = true;
          const transformedSlides = slidersArray.map((slider, index) => {
            let imageUrl = slider.imageUrl || slider.image || slider.img || slider.photo || slider.url ||
              'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80';

            return {
              id: slider.id || index + 1,
              title: slider.title || slider.heading || slider.headline || 'Welcome to Anusaya',
              subtitle: slider.subtitle || slider.subheading || '',
              description: slider.description || slider.desc || slider.text || 'Premium healthcare products',
              buttonText: slider.buttonText || slider.button || slider.cta || "Learn More",
              image: imageUrl,
              bgColor: slider.bgColor || slider.backgroundColor || slider.gradient || defaultGradients[index % defaultGradients.length],
              icon: slider.icon || <GiMedicinePills />,
              animation: slider.animation || "fadeIn"
            };
          });

          setSlides(transformedSlides);
          setCurrentSlide(0);
        }
      }
      
      // If we didn't find valid data in the first path, we can still fall back 
      // but let's just clear the loading state for now if we didn't find it
      if (!foundValidData) {
         console.log("No valid slider data found at Dr/homepage/sliders");
      }
      
      setLoadingSliders(false);
    });

    return () => unsubscribe();
  }, []);

  // Fetch Featured Products (Latest 4)
  useEffect(() => {
    const productsRef = dbRef(database, 'Dr/products');
    const unsubscribe = onValue(productsRef, (snapshot) => {
      if (snapshot.exists()) {
        const productsData = snapshot.val();
        // Convert object to array and sort by addedAt (newest first)
        const productsArray = Object.values(productsData)
          .filter(product => product.status === 'In Stock')
          .sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt))
          .slice(0, 4) // Get latest 4
          .map((product, index) => ({
            id: product.productId,
            name: product.productName,
            price: product.finalPrice || product.price,
            discountPrice: product.finalPrice || product.price,
            originalPrice: product.discountValue ? product.price : null,
            rating: 4.5, // Default rating
            image: product.imageUrl || 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
            category: product.manufacturer || 'Healthcare',
            description: product.description || 'Quality healthcare product',
            badge: product.discountValue ? `${product.discountValue}${product.discountType === 'percentage' ? '%' : '₹'} OFF` : 'NEW',
            color: ['bg-blue-100', 'bg-teal-100', 'bg-amber-100', 'bg-purple-100'][index % 4],
            inStock: product.status === 'In Stock' || product.stockQuantity > 0
          }));
        setFeaturedProducts(productsArray);
      }
      setLoadingProducts(false);
    });
    return () => unsubscribe();
  }, []);

  // Fetch Hospital Data
  useEffect(() => {
    const hospitalRef = dbRef(database, 'Dr/hospital');
    const unsubscribe = onValue(hospitalRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        // Bulletproof validation for the image URL to filter out 'undefined', 'null', or broken values
        const isUrlValid = (url) => {
          return url && 
                 typeof url === 'string' && 
                 url.trim() !== '' && 
                 url !== 'undefined' && 
                 url !== 'null' && 
                 (url.startsWith('http://') || url.startsWith('https://'));
        };
        
        const validatedImage = isUrlValid(data.image) 
          ? data.image 
          : 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80';

        setHospitalData({
          name: data.name || 'City General Hospital',
          address: data.address || '',
          phone: data.contactNumber || '',
          emergency: data.emergencyAvailable || false,
          departments: data.diseasesTreated?.map(d => d.name) || [],
          rating: 4.8,
          reviews: '2.4k',
          image: validatedImage,
          timings: `${data.workingHours?.mondayToFriday || '24/7 Emergency'}`,
          specialists: data.doctors?.length || 0,
          beds: 200
        });
      }
      setLoadingHospital(false);
    });
    return () => unsubscribe();
  }, []);

  // Fetch Diet Plans (First 4)
  useEffect(() => {
    const dietPlansRef = dbRef(database, 'Dr/dietPlans');
    const unsubscribe = onValue(dietPlansRef, (snapshot) => {
      if (snapshot.exists()) {
        const plansData = snapshot.val();
        const plansArray = Object.values(plansData)
          .filter(plan => plan.isActive)
          .slice(0, 4)
          .map((plan, index) => ({
            id: plan.planId,
            name: plan.diseaseName,
            description: plan.description?.substring(0, 60) + '...' || 'Customized meal plans',
            image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            color: ['from-red-100 to-pink-100', 'from-blue-100 to-indigo-100', 'from-pink-100 to-rose-100', 'from-green-100 to-emerald-100'][index % 4],
            icon: <FaHeart className={['text-red-500', 'text-blue-500', 'text-pink-500', 'text-green-500'][index % 4]} />,
            duration: `${plan.duration} Weeks`
          }));
        setDietPlans(plansArray);
      }
      setLoadingDietPlans(false);
    });
    return () => unsubscribe();
  }, []);

  // Fetch E-Books (First 3)
  useEffect(() => {
    const ebooksRef = dbRef(database, 'Dr/eBooks');
    const unsubscribe = onValue(ebooksRef, (snapshot) => {
      if (snapshot.exists()) {
        const ebooksData = snapshot.val();
        const ebooksArray = Object.values(ebooksData)
          .filter(book => book.isActive)
          .slice(0, 3)
          .map(book => ({
            id: book.ebookId,
            title: book.bookName,
            author: book.authorName,
            type: book.bookType,
            price: book.bookType === 'Paid' ? `₹${book.discountedPrice || book.bookPrice}` : null,
            pages: `${book.totalPages} Pages`,
            rating: 4.8,
            cover: book.coverUrl,
            category: book.language || 'Health'
          }));
        setEbooks(ebooksArray);
      }
      setLoadingEbooks(false);
    });
    return () => unsubscribe();
  }, []);

  // Fetch payment QR codes from Firebase
  useEffect(() => {
    const qrRef = dbRef(database, 'Dr/paymentQR');

    const unsubscribe = onValue(qrRef, (snapshot) => {
      try {
        if (snapshot.exists()) {
          const data = snapshot.val();
          setPaymentQRCodes({
            googlePay: data.googlePay || '',
            phonePe: data.phonePe || ''
          });
        }
        setQrLoading(false);
      } catch (err) {
        console.error('Error fetching payment QR codes:', err);
        setQrLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Features data
  const features = [
    {
      id: 1,
      title: "Trusted Products",
      description: "Verified & quality checked healthcare products",
      icon: <FaCheckCircle />,
      color: "text-green-500",
      gradient: "from-green-400 to-emerald-500",
      delay: 0.1
    },
    {
      id: 2,
      title: "Expert Hospital",
      description: "Partnered with top-rated medical facility",
      icon: <FaHospital />,
      color: "text-blue-500",
      gradient: "from-blue-400 to-cyan-500",
      delay: 0.2
    },
    {
      id: 3,
      title: "Diet Plans",
      description: "Nutritionist-approved personalized plans",
      icon: <FaUserMd />,
      color: "text-purple-500",
      gradient: "from-purple-400 to-violet-500",
      delay: 0.3
    },
    {
      id: 4,
      title: "Secure Payment",
      description: "100% safe & encrypted transactions",
      icon: <FaCreditCard />,
      color: "text-teal-500",
      gradient: "from-teal-400 to-emerald-500",
      delay: 0.4
    },
    {
      id: 5,
      title: "Easy Access",
      description: "Simple & intuitive platform",
      icon: <FaMobileAlt />,
      color: "text-orange-500",
      gradient: "from-orange-400 to-amber-500",
      delay: 0.5
    },
    {
      id: 6,
      title: "Fast Delivery",
      description: "Quick doorstep delivery",
      icon: <FaTruck />,
      color: "text-red-500",
      gradient: "from-red-400 to-rose-500",
      delay: 0.6
    }
  ];

  // Statistics data
  const stats = [
    { id: 1, value: "10K+", label: "Happy Customers", icon: <FaHeart />, color: "text-red-500" },
    { id: 2, value: "500+", label: "Products", icon: <GiMedicinePills />, color: "text-blue-500" },
    { id: 3, value: "45+", label: "Specialists", icon: <GiDoctorFace />, color: "text-green-500" },
    { id: 4, value: "24/7", label: "Support", icon: <FaBolt />, color: "text-yellow-500" }
  ];

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const slideIn = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  // Slider navigation
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  // Auto slide every 7 seconds
  useEffect(() => {
    if (slides.length <= 1) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 7000);
    return () => clearInterval(interval);
  }, [currentSlide, slides.length]);

  // Load cart on mount
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const userMobile = localStorage.getItem('userMobile');

    if (userId && userMobile) {
      const loadCartFromFirebase = async () => {
        try {
          const cartRef = dbRef(database, `Dr/users/${userMobile}/cart`);
          const snapshot = await get(cartRef);

          if (snapshot.exists()) {
            const cartData = snapshot.val();
            const cartArray = Object.keys(cartData).map(key => ({
              ...cartData[key],
              firebaseId: key
            }));
            setCart(cartArray);
          } else {
            setCart([]);
          }
        } catch (error) {
          console.error('Error loading cart from Firebase:', error);
          const cartData = JSON.parse(localStorage.getItem(`cart_${userId}`) || '[]');
          setCart(cartData);
        }
      };

      loadCartFromFirebase();
    } else {
      const guestCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
      setCart(guestCart);
    }
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  // Handle Add to Cart
  const handleAddToCart = (product) => {
    const userId = localStorage.getItem('userId');
    const userName = localStorage.getItem('userName');
    const userMobile = localStorage.getItem('userMobile');

    const isLoggedIn = userId && userMobile;

    if (isLoggedIn) {
      const addToFirebase = async () => {
        try {
          const cartRef = dbRef(database, `Dr/users/${userMobile}/cart`);
          const snapshot = await get(cartRef);

          let cartData = {};
          if (snapshot.exists()) {
            cartData = snapshot.val();
          }

          let existingItemKey = null;
          let existingItem = null;

          Object.keys(cartData).forEach(key => {
            if (cartData[key].id === product.id) {
              existingItemKey = key;
              existingItem = cartData[key];
            }
          });

          if (existingItem) {
            cartData[existingItemKey] = {
              ...existingItem,
              quantity: existingItem.quantity + 1,
              updatedAt: new Date().toISOString()
            };
          } else {
            const newItemKey = `item_${product.id}_${Date.now()}`;
            cartData[newItemKey] = {
              id: product.id,
              name: product.name,
              price: product.discountPrice,
              quantity: 1,
              image: product.image,
              category: product.category,
              description: product.description,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };
          }

          await set(cartRef, cartData);

          const cartArray = Object.keys(cartData).map(key => ({
            ...cartData[key],
            firebaseId: key
          }));
          setCart(cartArray);

          alert(`${product.name} added to cart successfully!`);

        } catch (error) {
          console.error('Error adding to cart:', error);
          alert('Failed to add to cart. Please try again.');
        }
      };

      addToFirebase();
    } else {
      try {
        const guestCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
        const existingItemIndex = guestCart.findIndex(item => item.id === product.id);

        if (existingItemIndex >= 0) {
          guestCart[existingItemIndex].quantity += 1;
        } else {
          const cartItem = {
            id: product.id,
            name: product.name,
            price: product.discountPrice,
            quantity: 1,
            image: product.image,
            category: product.category,
            description: product.description
          };
          guestCart.push(cartItem);
        }

        localStorage.setItem('guest_cart', JSON.stringify(guestCart));
        setCart(guestCart);

        alert(`${product.name} added to cart successfully!`);
        navigate('/cart');

      } catch (error) {
        console.error('Error adding to guest cart:', error);
        alert('Failed to add to cart.');
      }
    }
  };

  // Handle Buy Now
  const handleBuyNow = (product) => {
    const userId = localStorage.getItem('userId');
    const userMobile = localStorage.getItem('userMobile');

    if (!userId || !userMobile) {
      navigate('/login');
      return;
    }

    console.log('Buy Now clicked - Opening payment modal');
    setSelectedProduct(product);
    setQuantity(1);
    setShowPaymentModal(true);
    setPaymentStep(1);

    if (userMobile) {
      setPhoneNumber(userMobile);
    }
  };

  // Payment handler functions
  const handlePaymentScreenshotUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setPaymentScreenshot(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPaymentScreenshotPreview(reader.result);
        };
        reader.readAsDataURL(file);
        setFormErrors(prev => ({ ...prev, screenshot: '' }));
      } else {
        setFormErrors(prev => ({ ...prev, screenshot: 'Please upload a valid image file' }));
      }
    }
  };

  const validateDeliveryDetails = () => {
    const errors = {};

    if (!deliveryAddress.trim() || deliveryAddress.trim().length < 10) {
      errors.address = 'Please enter a complete delivery address (minimum 10 characters)';
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneNumber.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!phoneRegex.test(phoneNumber.trim())) {
      errors.phone = 'Please enter a valid 10-digit Indian mobile number';
    }

    return errors;
  };

  const validatePaymentDetails = () => {
    const errors = {};

    if (!selectedPaymentMethod) {
      errors.paymentMethod = 'Please select a payment method';
    }

    if (!paymentScreenshot) {
      errors.screenshot = 'Please upload payment screenshot';
    }

    const upiRegex = /^[\w.-]+@[\w.-]+$/;
    if (!upiId.trim()) {
      errors.upiId = 'UPI ID is required';
    } else if (!upiRegex.test(upiId.trim())) {
      errors.upiId = 'Please enter a valid UPI ID (e.g., username@paytm)';
    }

    return errors;
  };

  const handleContinueToPayment = () => {
    const errors = validateDeliveryDetails();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setPaymentStep(2);
  };

  const handleSubmitOrder = async () => {
    const errors = validatePaymentDetails();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsProcessingOrder(true);
    setFormErrors({});

    try {
      // Get user details from localStorage
      const userId = localStorage.getItem('userId');
      const userName = localStorage.getItem('userName');
      const userMobile = localStorage.getItem('userMobile');

      console.log('Starting order submission for mobile:', userMobile);

      // Step 1: Upload payment screenshot to Firebase Storage
      let screenshotURL = '';
      try {
        const timestamp = Date.now();
        const screenshotFileName = `payment_${userMobile}_${timestamp}_${paymentScreenshot.name}`;
        const screenshotStorageRef = storageRef(storage, `Dr/orders/${userMobile}/screenshots/${screenshotFileName}`);

        console.log('Uploading screenshot to:', `Dr/orders/${userMobile}/screenshots/${screenshotFileName}`);

        // Upload the file
        const uploadResult = await uploadBytes(screenshotStorageRef, paymentScreenshot);
        console.log('Screenshot uploaded successfully');

        // Get the download URL
        screenshotURL = await getDownloadURL(uploadResult.ref);
        console.log('Screenshot URL:', screenshotURL);
      } catch (uploadError) {
        console.error('Error uploading screenshot:', uploadError);
        setFormErrors({ submit: 'Failed to upload payment screenshot. Please try again.' });
        setIsProcessingOrder(false);
        return;
      }

      // Step 2: Get the next order number for this mobile number
      const userOrdersRef = dbRef(database, `Dr/orders/${userMobile}`);
      const userOrdersSnapshot = await get(userOrdersRef);

      let nextOrderNumber = 1;
      if (userOrdersSnapshot.exists()) {
        const existingOrders = userOrdersSnapshot.val();
        // Get all numeric keys and find the maximum
        const orderNumbers = Object.keys(existingOrders)
          .map(key => parseInt(key))
          .filter(num => !isNaN(num));

        if (orderNumbers.length > 0) {
          nextOrderNumber = Math.max(...orderNumbers) + 1;
        }
      }

      console.log('Next order number for mobile', userMobile, ':', nextOrderNumber);

      // Step 3: Create order object
      const orderData = {
        orderId: `ORD${Date.now()}`,
        orderNumber: nextOrderNumber,
        userId: userId,
        userName: userName,
        userMobile: userMobile,
        product: {
          id: selectedProduct.id,
          name: selectedProduct.name,
          price: selectedProduct.discountPrice,
          quantity: quantity,
          totalPrice: selectedProduct.discountPrice * quantity,
          image: selectedProduct.image,
          category: selectedProduct.category,
          description: selectedProduct.description
        },
        deliveryDetails: {
          address: deliveryAddress,
          phoneNumber: phoneNumber
        },
        paymentDetails: {
          method: selectedPaymentMethod,
          upiId: upiId,
          screenshotURL: screenshotURL,
          screenshotName: paymentScreenshot.name,
          screenshotSize: paymentScreenshot.size
        },
        orderStatus: 'Pending Verification',
        paymentStatus: 'Pending Verification',
        timestamp: new Date().toISOString(),
        createdAt: Date.now()
      };

      // Step 4: Save order to Firebase under mobile number with sequential number
      const orderRef = dbRef(database, `Dr/orders/${userMobile}/${nextOrderNumber}`);
      await set(orderRef, orderData);

      console.log('Order saved successfully at:', `Dr/orders/${userMobile}/${nextOrderNumber}`);

      setOrderId(orderData.orderId);
      setOrderSuccess(true);
      setPaymentStep(3);
      setIsProcessingOrder(false);

      // Reset form after 5 seconds
      setTimeout(() => {
        handleClosePaymentModal();
      }, 5000);

    } catch (error) {
      console.error('Error submitting order:', error);
      setFormErrors({ submit: 'Failed to submit order. Please try again.' });
      setIsProcessingOrder(false);
    }
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentStep(1);
    setDeliveryAddress('');
    setPhoneNumber('');
    setSelectedPaymentMethod('');
    setPaymentScreenshot(null);
    setPaymentScreenshotPreview('');
    setUpiId('');
    setFormErrors({});
    setOrderSuccess(false);
    setOrderId('');
  };

  const handleBackToDelivery = () => {
    setPaymentStep(1);
    setFormErrors({});
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      <section className="relative w-full overflow-hidden bg-transparent">

        {/* CSS keyframes for loader animations */}
        <style>{`
          @keyframes sliderSpin { to { transform: rotate(360deg); } }
          @keyframes sliderPulse {
            0%, 100% { transform: scale(1); opacity: 0.5; }
            50% { transform: scale(1.15); opacity: 1; }
          }
        `}</style>

        {/* ── LOADER ── shown while Firebase loading OR while first image is downloading */}
        {(loadingSliders || (slides.length > 0 && !sliderImagesReady)) && (
          <div
            style={{
              position: slides.length > 0 && !loadingSliders ? 'absolute' : 'relative',
              top: 0, left: 0, right: 0, bottom: 0,
              zIndex: 20,
              minHeight: '220px',
              background: 'linear-gradient(135deg, #1a237e 0%, #0277bd 40%, #00897b 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '20px'
            }}
          >
            <div style={{ position: 'relative', width: '72px', height: '72px' }}>
              <div style={{
                position: 'absolute', inset: 0,
                borderRadius: '50%',
                border: '4px solid rgba(255,255,255,0.15)',
                animation: 'sliderPulse 1.5s ease-in-out infinite'
              }} />
              <div style={{
                position: 'absolute', inset: '8px',
                borderRadius: '50%',
                border: '4px solid rgba(255,255,255,0.25)',
                animation: 'sliderPulse 1.5s ease-in-out 0.2s infinite'
              }} />
              <div style={{
                position: 'absolute', inset: '18px',
                borderRadius: '50%',
                border: '3px solid transparent',
                borderTopColor: '#ffffff',
                borderRightColor: '#ffffff',
                animation: 'sliderSpin 0.9s linear infinite'
              }} />
            </div>
            <p style={{
              color: 'rgba(255,255,255,0.85)',
              fontSize: '15px',
              fontWeight: 500,
              letterSpacing: '0.05em'
            }}>Loading...</p>
          </div>
        )}

        {/* ── SLIDER ── rendered invisibly so images start downloading immediately */}
        {!loadingSliders && slides.length > 0 && (
          <div
            className="relative overflow-hidden w-full h-auto min-h-[200px] sm:min-h-[300px]"
            style={{
              opacity: sliderImagesReady ? 1 : 0,
              transition: 'opacity 0.5s ease'
            }}
          >
            <motion.div
              className="flex w-full h-full"
              animate={{ x: `-${currentSlide * 100}%` }}
              transition={{ x: { type: "tween", duration: 0.8, ease: "easeInOut" } }}
            >
              {slides.map((slide, index) => (
                <div key={slide.id || index} className="w-full h-auto flex-shrink-0 relative">
                  <img
                    src={slide.image}
                    alt="Hero Slide"
                    className="w-full h-auto block"
                    onLoad={index === 0 ? () => setSliderImagesReady(true) : undefined}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/10 pointer-events-none" />
                </div>
              ))}
            </motion.div>

            {/* Slider Navigation Arrows */}
            {slides.length > 1 && (
              <>
                <button
                  onClick={prevSlide}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-black/30 hover:bg-black/50 backdrop-blur-md text-white p-3 rounded-full transition-all duration-300 border border-white/10 hover:scale-110 active:scale-95 cursor-pointer"
                  aria-label="Previous Slide"
                >
                  <FaArrowLeft className="text-lg md:text-xl" />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-black/30 hover:bg-black/50 backdrop-blur-md text-white p-3 rounded-full transition-all duration-300 border border-white/10 hover:scale-110 active:scale-95 cursor-pointer"
                  aria-label="Next Slide"
                >
                  <FaArrowRight className="text-lg md:text-xl" />
                </button>
              </>
            )}

            {/* Slide Indicators */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-4 z-20">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/80'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* ── NO SLIDERS ── */}
        {!loadingSliders && slides.length === 0 && (
          <div className="flex items-center justify-center bg-gradient-to-r from-gray-600 to-gray-800" style={{ minHeight: '220px' }}>
            <div className="text-center text-white">
              <p className="text-2xl font-semibold">No sliders available</p>
            </div>
          </div>
        )}

      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gradient-to-r from-blue-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {stats.map((stat) => (
              <motion.div
                key={stat.id}
                variants={fadeInUp}
                whileHover={{ scale: 1.05, y: -5 }}
                className="bg-white rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className={`text-4xl mb-3 ${stat.color}`}>
                  {stat.icon}
                </div>
                <motion.div
                  className="text-3xl md:text-4xl font-bold text-gray-800 mb-2"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: stat.id * 0.1, type: "spring" }}
                >
                  {stat.value}
                </motion.div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
              Featured Healthcare Products
            </h2>
            <div className="w-24 h-2 bg-gradient-to-r from-blue-500 to-teal-500 mx-auto mb-8 rounded-full"></div>
            <p className="text-xl text-gray-600">Premium medical equipment and essentials</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {featuredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{
                  y: -15,
                  transition: { duration: 0.3 }
                }}
                className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden group"
              >
                <div className="h-56 overflow-hidden relative">
                  <motion.img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    whileHover={{ scale: 1.1 }}
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${product.color} text-gray-800`}>
                      {product.category}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-red-500 text-white rounded-full text-sm font-bold animate-pulse">
                      {product.badge}
                    </span>
                  </div>
                </div>
                <div className="p-6 flex flex-col h-[calc(100%-14rem)]">
                  <div className="flex-grow">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-xl font-bold text-gray-800">{product.name}</h3>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900">₹{product.price}</div>
                        {product.originalPrice && (
                          <div className="text-sm text-gray-500 line-through">₹{product.originalPrice}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center mb-6">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <FaStar
                            key={i}
                            className={`${i < Math.floor(product.rating) ? 'text-yellow-400' : 'text-gray-300'} mr-1`}
                            size={18}
                          />
                        ))}
                      </div>
                      <span className="text-gray-600 ml-2 text-sm">({product.rating}/5)</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-3 mt-auto">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleBuyNow(product)}
                      className="flex items-center justify-center space-x-1 bg-gray-100 text-gray-700 px-3 py-2.5 rounded-xl hover:bg-gray-200 transition-colors text-sm"
                    >
                      <FaShoppingCart size={14} />
                      <span className="font-medium">Buy Now</span>
                    </motion.button>

                    <motion.button
                      id={`add-to-cart-${product.id}`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => product.inStock && handleAddToCart(product)}
                      disabled={!product.inStock}
                      className={`flex items-center justify-center space-x-1 px-3 py-2.5 rounded-xl font-medium text-sm ${product.inStock
                        ? "bg-gradient-to-r from-teal-500 to-emerald-500 text-white hover:shadow-lg"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                        }`}
                    >
                      <FaShoppingCart size={14} />
                      <span>Add to Cart</span>
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/products')}
              className="bg-gradient-to-r from-blue-600 to-teal-600 text-white px-12 py-5 rounded-full font-bold text-lg hover:shadow-2xl transition duration-300 shadow-lg relative overflow-hidden group"
            >
              <span className="relative z-10 flex items-center">
                Explore All Products
                <FaArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* About Platform Section */}
      <section className="py-20 px-4 md:px-8" id="about-section">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={scaleIn}
          className="max-w-7xl mx-auto"
        >
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent"
            >
              Your Complete Health Ecosystem
            </motion.h2>
            <div className="w-24 h-2 bg-gradient-to-r from-blue-500 to-teal-500 mx-auto mb-8 rounded-full"></div>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              A revolutionary platform combining medical products, expert care, and personalized wellness solutions
            </motion.p>
          </div>

          <div className="bg-gradient-to-br from-white to-blue-50 p-8 md:p-12 rounded-3xl shadow-2xl border border-blue-100 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-200 rounded-full opacity-10 blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-teal-200 rounded-full opacity-10 blur-3xl"></div>

            <div className="relative z-10 flex flex-col lg:flex-row items-center">
              <div className="lg:w-1/2 mb-10 lg:mb-0 lg:pr-12">
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  className="space-y-6"
                >
                  <h3 className="text-3xl font-bold text-gray-800">One Platform, Complete Care</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    <span className="font-bold text-blue-600">Anusaya Platform</span> brings together everything you need for comprehensive healthcare management. From{" "}
                    <span className="font-bold text-teal-600">verified medical products</span> to{" "}
                    <span className="font-bold text-purple-600">specialist consultations</span>,{" "}
                    <span className="font-bold text-green-600">personalized diet plans</span>, and{" "}
                    <span className="font-bold text-indigo-600">educational resources</span> - all seamlessly integrated.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {["Telemedicine", "Home Delivery", "24/7 Support", "Insurance", "AI Health Assistant"].map((feature, i) => (
                      <motion.span
                        key={i}
                        initial={{ opacity: 0, scale: 0 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.1 }}
                        whileHover={{ scale: 1.1 }}
                        className="px-4 py-2 bg-gradient-to-r from-blue-100 to-teal-100 text-blue-700 rounded-full font-medium border border-blue-200"
                      >
                        {feature}
                      </motion.span>
                    ))}
                  </div>
                </motion.div>
              </div>
              <div className="lg:w-1/2">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
                  whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                  transition={{ duration: 0.6 }}
                  className="relative"
                >
                  <img
                    src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                    alt="Healthcare Platform"
                    className="rounded-2xl shadow-2xl transform hover:rotate-1 transition-transform duration-300"
                  />
                  <motion.div
                    animate={{
                      y: [0, -15, 0],
                      rotate: [0, 5, 0]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="absolute -top-6 -right-6 bg-white p-6 rounded-2xl shadow-xl"
                  >
                    <GiMedicinePills className="text-5xl text-teal-600" />
                  </motion.div>
                  <motion.div
                    animate={{
                      y: [0, 15, 0],
                      rotate: [0, -5, 0]
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 0.5
                    }}
                    className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl"
                  >
                    <GiStethoscope className="text-5xl text-blue-600" />
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 md:px-8 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
              Why Choose Anusaya
            </motion.h2>
            <motion.div variants={fadeInUp} className="w-24 h-2 bg-gradient-to-r from-blue-500 to-teal-500 mx-auto mb-8 rounded-full"></motion.div>
            <motion.p variants={fadeInUp} className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience healthcare reimagined with cutting-edge features
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {features.map((feature) => (
              <motion.div
                key={feature.id}
                variants={fadeInUp}
                whileHover={{
                  scale: 1.05,
                  y: -10,
                  transition: { duration: 0.3 }
                }}
                className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 from-blue-50 to-teal-50"></div>
                <div className="relative z-10">
                  <motion.div
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                    className={`inline-flex p-4 rounded-2xl mb-6 bg-gradient-to-br ${feature.gradient} bg-opacity-10`}
                  >
                    <div className={`text-4xl ${feature.color}`}>
                      {feature.icon}
                    </div>
                  </motion.div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: "100%" }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="h-1 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full mt-4"
                  />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      

      {/* Hospital Section */}
      <section className="py-20 px-4 md:px-8 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
              Our Partner Hospital
            </h2>
            <div className="w-24 h-2 bg-gradient-to-r from-emerald-500 to-green-500 mx-auto mb-8 rounded-full"></div>
            <p className="text-xl text-gray-600">Trusted medical care with advanced facilities</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-3xl shadow-2xl overflow-hidden border border-emerald-100"
          >
            {loadingHospital ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-emerald-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading hospital information...</p>
                </div>
              </div>
            ) : hospitalData ? (
              <div className="md:flex">
                <div className="md:w-1/2 p-8 md:p-12">
                  <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    className="space-y-8"
                  >
                    <div className="flex items-center">
                      <div className="bg-white p-4 rounded-2xl shadow-lg mr-6">
                        <FaHospital className="text-4xl text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="text-3xl font-bold text-gray-800">{hospitalData.name}</h3>
                        <div className="flex items-center mt-2">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <FaStar key={i} className="text-yellow-400 mr-1" size={20} />
                            ))}
                          </div>
                          <span className="text-gray-600 ml-2 font-semibold">{hospitalData.rating}/5 • {hospitalData.reviews} reviews</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center">
                        <FaMapMarkerAlt className="text-emerald-500 mr-4" />
                        <span className="text-gray-700">{hospitalData.address}</span>
                      </div>
                      <div className="flex items-center">
                        <FaPhoneAlt className="text-emerald-500 mr-4" />
                        <span className="text-gray-700 font-semibold">{hospitalData.phone}</span>
                      </div>
                      <div className="flex items-center">
                        <FaClock className="text-emerald-500 mr-4" />
                        <span className="text-gray-700">{hospitalData.timings}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-8">
                      <div className="bg-white p-4 rounded-xl shadow-sm text-center">
                        <div className="text-2xl font-bold text-emerald-600">{hospitalData.specialists}+</div>
                        <div className="text-gray-600">Specialists</div>
                      </div>
                      <div className="bg-white p-4 rounded-xl shadow-sm text-center">
                        <div className="text-2xl font-bold text-emerald-600">{hospitalData.beds}</div>
                        <div className="text-gray-600">Beds</div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-xl font-bold text-gray-800 mb-4">Specialized Departments:</h4>
                      <div className="grid grid-cols-2 gap-3">
                        {hospitalData.departments.map((dept, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ delay: index * 0.1 }}
                            whileHover={{ scale: 1.05 }}
                            className="bg-white px-4 py-3 rounded-xl shadow-sm border border-emerald-100 hover:border-emerald-300 transition-colors"
                          >
                            <span className="font-medium text-gray-700">{dept}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-xl transition duration-300 flex items-center justify-center group"
                    >
                      <FaHospital className="mr-3 group-hover:scale-110 transition-transform" />
                      Book Appointment →
                    </motion.button>
                  </motion.div>
                </div>

                <div className="md:w-1/2 flex items-center justify-center bg-white" style={{ minHeight: '500px', padding: '24px' }}>
                  <img
                    src={anusayaNewImg}
                    alt={hospitalData.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      maxHeight: '500px',
                      objectFit: 'contain',
                      objectPosition: 'center center',
                      display: 'block'
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-20">
                <p className="text-gray-600">No hospital information available</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Diet Plans Section */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Personalized Diet Plans
            </h2>
            <div className="w-24 h-2 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto mb-8 rounded-full"></div>
            <p className="text-xl text-gray-600">Nutritionist-approved plans for specific health conditions</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {dietPlans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 50, rotateY: 90 }}
                whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{
                  y: -15,
                  rotateY: 5,
                  transition: { duration: 0.3 }
                }}
                className={`bg-gradient-to-br ${plan.color} rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 border border-white relative group`}
              >
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="mr-3">
                      {plan.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800">{plan.name}</h3>
                  </div>
                  <p className="text-gray-600 mb-6">{plan.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="px-3 py-1 bg-white/50 rounded-full text-sm font-medium">
                      {plan.duration}
                    </span>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:shadow-lg transition duration-300 text-sm font-semibold"
                    >
                      View Plan
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/diet-plans')}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-12 py-5 rounded-full font-bold text-lg hover:shadow-2xl transition duration-300 shadow-lg flex items-center mx-auto group"
            >
              <FaAppleAlt className="mr-3 group-hover:rotate-12 transition-transform" />
              Get Your Custom Plan →
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* E-Books Section */}
      <section className="py-20 px-4 md:px-8 bg-gradient-to-b from-white to-indigo-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
              Health & Wellness E-Books
            </h2>
            <div className="w-24 h-2 bg-gradient-to-r from-indigo-500 to-blue-500 mx-auto mb-8 rounded-full"></div>
            <p className="text-xl text-gray-600">Expert knowledge for better health decisions</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {ebooks.map((book, index) => (
              <motion.div
                key={book.id}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{
                  y: -10,
                  transition: { duration: 0.3 }
                }}
                className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden group"
              >
                <div className="h-64 overflow-hidden relative">
                  <img
                    src={book.cover}
                    alt={book.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-1">{book.title}</h3>
                      <p className="text-gray-600 text-sm">By {book.author}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${book.type === 'Free' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {book.type === 'Free' ? 'FREE' : `₹${book.price}`}
                    </span>
                  </div>

                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center">
                      <div className="flex mr-2">
                        {[...Array(5)].map((_, i) => (
                          <FaStar
                            key={i}
                            className={`${i < Math.floor(book.rating) ? 'text-yellow-400' : 'text-gray-300'} mr-1`}
                            size={14}
                          />
                        ))}
                      </div>
                      <span className="text-gray-600 text-sm">{book.rating}/5</span>
                    </div>
                    <span className="text-gray-500 text-sm">{book.pages}</span>
                  </div>

                  <div className="mb-4">
                    <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm">
                      {book.category}
                    </span>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full bg-gradient-to-r from-indigo-500 to-blue-500 text-white px-6 py-3 rounded-xl hover:from-indigo-600 hover:to-blue-600 transition duration-300 flex items-center justify-center font-semibold group"
                  >
                    <FaBookMedical className="mr-3 group-hover:scale-110 transition-transform" />
                    {book.type === 'Free' ? 'Download Now' : 'Purchase E-Book'}
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-12"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/ebooks')}
              className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-12 py-5 rounded-full font-bold text-lg hover:shadow-2xl transition duration-300 shadow-lg flex items-center mx-auto group"
            >
              <FaBookMedical className="mr-3 group-hover:scale-110 transition-transform" />
              View All E-books →
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="max-w-6xl mx-auto text-center"
        >
          <div className="bg-gradient-to-r from-blue-600 to-teal-600 rounded-3xl p-12 md:p-16 shadow-2xl relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6"
            >
              Start Your Health Journey Today
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-white/90 mb-10 max-w-2xl mx-auto"
            >
              Join thousands who have transformed their health with our comprehensive platform
            </motion.p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-blue-600 px-12 py-5 rounded-full font-bold text-lg hover:shadow-2xl transition duration-300 shadow-lg group"
            >
              <span className="flex items-center">
                Get Started Free
                <FaRocket className="ml-3 group-hover:translate-x-2 transition-transform" />
              </span>
            </motion.button>
          </div>
        </motion.div>
      </section>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col md:flex-row"
            >
              {/* Product Summary Side */}
              <div className="md:w-1/3 bg-gray-50 p-6 border-r border-gray-100 overflow-y-auto">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Order Summary</h3>
                {selectedProduct && (
                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-xl shadow-sm">
                      <img
                        src={selectedProduct.image}
                        alt={selectedProduct.name}
                        className="w-full h-32 object-cover rounded-lg mb-3"
                      />
                      <h4 className="font-semibold text-gray-800 line-clamp-2">{selectedProduct.name}</h4>
                      <p className="text-sm text-gray-500 mb-2">{selectedProduct.category}</p>
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-teal-600">₹{selectedProduct.discountPrice || selectedProduct.price}</span>
                        <div className="flex items-center space-x-2 bg-gray-100 rounded-lg px-2 py-1">
                          <span className="text-sm font-medium">Qty: {quantity}</span>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4 space-y-2">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Subtotal</span>
                        <span>₹{(selectedProduct.discountPrice || selectedProduct.price) * quantity}</span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Delivery Fee</span>
                        <span className="text-green-600">Free</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg text-gray-800 pt-2 border-t border-gray-200">
                        <span>Total</span>
                        <span>₹{(selectedProduct.discountPrice || selectedProduct.price) * quantity}</span>
                      </div>
                    </div>

                    {/* Trust Badges */}
                    <div className="grid grid-cols-2 gap-2 mt-4">
                      <div className="flex items-center text-xs text-gray-500 bg-white p-2 rounded-lg border border-gray-100">
                        <FaShieldAlt className="text-emerald-500 mr-2" />
                        Secure
                      </div>
                      <div className="flex items-center text-xs text-gray-500 bg-white p-2 rounded-lg border border-gray-100">
                        <FaCheckCircle className="text-blue-500 mr-2" />
                        Verified
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Payment Details Side */}
              <div className="md:w-2/3 p-6 overflow-y-auto relative">
                <button
                  onClick={handleClosePaymentModal}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FaTimes size={20} />
                </button>

                {/* Steps Indicator */}
                <div className="flex items-center justify-center mb-8">
                  <div className={`flex items-center ${paymentStep >= 1 ? 'text-teal-600' : 'text-gray-300'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${paymentStep >= 1 ? 'border-teal-600 bg-teal-50' : 'border-gray-300'}`}>
                      {paymentStep > 1 ? <FaCheck size={14} /> : '1'}
                    </div>
                    <span className="ml-2 font-medium text-sm">Delivery</span>
                  </div>
                  <div className={`w-16 h-1 mx-4 ${paymentStep >= 2 ? 'bg-teal-600' : 'bg-gray-200'}`}></div>
                  <div className={`flex items-center ${paymentStep >= 2 ? 'text-teal-600' : 'text-gray-300'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${paymentStep >= 2 ? 'border-teal-600 bg-teal-50' : 'border-gray-300'}`}>
                      {paymentStep > 2 ? <FaCheck size={14} /> : '2'}
                    </div>
                    <span className="ml-2 font-medium text-sm">Payment</span>
                  </div>
                  <div className={`w-16 h-1 mx-4 ${paymentStep >= 3 ? 'bg-teal-600' : 'bg-gray-200'}`}></div>
                  <div className={`flex items-center ${paymentStep >= 3 ? 'text-teal-600' : 'text-gray-300'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${paymentStep >= 3 ? 'border-teal-600 bg-teal-50' : 'border-gray-300'}`}>
                      3
                    </div>
                    <span className="ml-2 font-medium text-sm">Review</span>
                  </div>
                </div>

                <div className="min-h-[400px]">
                  <AnimatePresence mode="wait">
                    {paymentStep === 1 && (
                      <motion.div
                        key="step1"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-6"
                      >
                        <h2 className="text-2xl font-bold text-gray-800">Delivery Details</h2>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Address</label>
                            <textarea
                              value={deliveryAddress}
                              onChange={(e) => setDeliveryAddress(e.target.value)}
                              placeholder="House No, Street, Area, City, Pincode"
                              className={`w-full px-4 py-3 rounded-xl border ${formErrors.address ? 'border-red-500 focus:ring-red-200' : 'border-gray-200 focus:ring-teal-200'} focus:border-teal-500 focus:ring-2 outline-none transition-all h-32 resize-none`}
                            />
                            {formErrors.address && <p className="text-red-500 text-xs mt-1">{formErrors.address}</p>}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                            <div className="relative">
                              <FaPhoneAlt className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                              <input
                                type="tel"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                placeholder="10-digit mobile number"
                                className={`w-full pl-12 pr-4 py-3 rounded-xl border ${formErrors.phone ? 'border-red-500 focus:ring-red-200' : 'border-gray-200 focus:ring-teal-200'} focus:border-teal-500 focus:ring-2 outline-none transition-all`}
                              />
                            </div>
                            {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
                          </div>
                        </div>

                        <button
                          onClick={handleContinueToPayment}
                          className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all"
                        >
                          Continue to Payment
                        </button>
                      </motion.div>
                    )}

                    {paymentStep === 2 && (
                      <motion.div
                        key="step2"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-6"
                      >
                        <div className="flex items-center space-x-2 mb-4">
                          <button onClick={handleBackToDelivery} className="text-gray-400 hover:text-gray-600">
                            <FaArrowLeft />
                          </button>
                          <h2 className="text-2xl font-bold text-gray-800">Payment Method</h2>
                        </div>

                        {/* Payment Options */}
                        <div className="grid grid-cols-2 gap-4">
                          <div
                            onClick={() => setSelectedPaymentMethod('UPI')}
                            className={`cursor-pointer p-4 rounded-xl border-2 transition-all flex flex-col items-center justify-center space-y-2 ${selectedPaymentMethod === 'UPI' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}
                          >
                            <img src="https://cdn-icons-png.flaticon.com/512/10095/10095460.png" alt="UPI" className="w-10 h-10" />
                            <span className="font-semibold text-gray-700">UPI</span>
                          </div>
                          <div
                            onClick={() => setSelectedPaymentMethod('COD')}
                            className={`cursor-pointer p-4 rounded-xl border-2 transition-all flex flex-col items-center justify-center space-y-2 opacity-50 cursor-not-allowed border-gray-200 bg-gray-50`}
                          >
                            <img src="https://cdn-icons-png.flaticon.com/512/2331/2331941.png" alt="COD" className="w-10 h-10 grayscale" />
                            <span className="font-semibold text-gray-500">Cash on Delivery</span>
                            <span className="text-xs text-red-500">(Currently Unavailable)</span>
                          </div>
                        </div>
                        {formErrors.paymentMethod && <p className="text-red-500 text-xs mt-1 text-center">{formErrors.paymentMethod}</p>}

                        {/* UPI Payment Section */}
                        {selectedPaymentMethod === 'UPI' && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="space-y-6 pt-4 border-t border-gray-200"
                          >
                            {/* QR Codes */}
                            <div className="flex justify-center space-x-6">
                              {/* Google Pay QR */}
                              <div className="text-center">
                                <p className="text-sm font-semibold mb-2 text-gray-600">Google Pay</p>
                                <div className="bg-white p-2 rounded-xl shadow-md border border-gray-100 relative w-32 h-32 flex items-center justify-center">
                                  {qrLoading ? (
                                    <FaSpinner className="animate-spin text-teal-500 text-2xl" />
                                  ) : (
                                    paymentQRCodes.googlePay ? (
                                      <img src={paymentQRCodes.googlePay} alt="GPay QR" className="w-full h-full object-contain" />
                                    ) : (
                                      <div className="flex flex-col items-center justify-center text-gray-400 text-xs">
                                        <FaQrcode className="text-2xl mb-1" />
                                        <span>No QR</span>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                              {/* PhonePe QR */}
                              <div className="text-center">
                                <p className="text-sm font-semibold mb-2 text-gray-600">PhonePe</p>
                                <div className="bg-white p-2 rounded-xl shadow-md border border-gray-100 relative w-32 h-32 flex items-center justify-center">
                                  {qrLoading ? (
                                    <FaSpinner className="animate-spin text-purple-500 text-2xl" />
                                  ) : (
                                    paymentQRCodes.phonePe ? (
                                      <img src={paymentQRCodes.phonePe} alt="PhonePe QR" className="w-full h-full object-contain" />
                                    ) : (
                                      <div className="flex flex-col items-center justify-center text-gray-400 text-xs">
                                        <FaQrcode className="text-2xl mb-1" />
                                        <span>No QR</span>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            </div>

                            <p className="text-center text-sm text-gray-500 bg-blue-50 p-2 rounded-lg">
                              Scan QR code to pay <strong>₹{(selectedProduct.discountPrice || selectedProduct.price) * quantity}</strong>
                            </p>

                            {/* UPI ID Input */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Your UPI ID</label>
                              <input
                                type="text"
                                value={upiId}
                                onChange={(e) => setUpiId(e.target.value)}
                                placeholder="e.g., username@okaxis"
                                className={`w-full px-4 py-3 rounded-xl border ${formErrors.upiId ? 'border-red-500 focus:ring-red-200' : 'border-gray-200 focus:ring-teal-200'} focus:border-teal-500 focus:ring-2 outline-none transition-all`}
                              />
                              {formErrors.upiId && <p className="text-red-500 text-xs mt-1">{formErrors.upiId}</p>}
                            </div>

                            {/* Screenshot Upload */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Upload Payment Screenshot</label>
                              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-xl hover:border-teal-400 transition-colors bg-gray-50">
                                <div className="space-y-1 text-center">
                                  {paymentScreenshotPreview ? (
                                    <div className="relative">
                                      <img src={paymentScreenshotPreview} alt="Preview" className="mx-auto h-48 object-contain rounded-lg" />
                                      <button
                                        onClick={() => {
                                          setPaymentScreenshot(null);
                                          setPaymentScreenshotPreview('');
                                        }}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                                      >
                                        <FaTimes size={12} />
                                      </button>
                                      <p className="text-xs text-green-600 mt-2 font-medium flex items-center justify-center">
                                        <FaCheckCircle className="mr-1" /> Image selected
                                      </p>
                                    </div>
                                  ) : (
                                    <>
                                      <FaUpload className="mx-auto h-12 w-12 text-gray-400" />
                                      <div className="flex text-sm text-gray-600">
                                        <label className="relative cursor-pointer bg-white rounded-md font-medium text-teal-600 hover:text-teal-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-teal-500">
                                          <span>Upload a file</span>
                                          <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handlePaymentScreenshotUpload} accept="image/*" />
                                        </label>
                                        <p className="pl-1">or drag and drop</p>
                                      </div>
                                      <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                                    </>
                                  )}
                                </div>
                              </div>
                              {formErrors.screenshot && <p className="text-red-500 text-xs mt-1 text-center">{formErrors.screenshot}</p>}
                            </div>

                            {formErrors.submit && (
                              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm flex items-center">
                                <FaTimesCircle className="mr-2" />
                                {formErrors.submit}
                              </div>
                            )}

                            <button
                              onClick={handleSubmitOrder}
                              disabled={isProcessingOrder}
                              className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg transition-all flex items-center justify-center ${isProcessingOrder
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white hover:shadow-xl hover:scale-[1.02]'
                                }`}
                            >
                              {isProcessingOrder ? (
                                <>
                                  <FaSpinner className="animate-spin mr-2" />
                                  Processing...
                                </>
                              ) : (
                                'Submit Order'
                              )}
                            </button>
                          </motion.div>
                        )}
                      </motion.div>
                    )}

                    {paymentStep === 3 && (
                      <motion.div
                        key="step3"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex flex-col items-center justify-center h-full py-10"
                      >
                        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-6">
                          <FaCheck className="text-5xl text-green-600" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800 mb-2">Order Successful!</h2>
                        <p className="text-gray-600 text-center mb-8">
                          Your order has been placed successfully.<br />
                          Order ID: <span className="font-mono font-bold text-gray-800">{orderId}</span>
                        </p>
                        <div className="bg-blue-50 p-6 rounded-xl w-full mb-8">
                          <h4 className="font-semibold text-blue-800 mb-2">What's Next?</h4>
                          <ul className="text-sm text-blue-700 space-y-2">
                            <li className="flex items-start">
                              <span className="mr-2">•</span>
                              We will verify your payment screenshot shortly.
                            </li>
                            <li className="flex items-start">
                              <span className="mr-2">•</span>
                              You will receive a confirmation call on your registered number.
                            </li>
                            <li className="flex items-start">
                              <span className="mr-2">•</span>
                              Your order will be dispatched within 2 hours.
                            </li>
                          </ul>
                        </div>
                        <div className="flex space-x-4">
                          <button
                            onClick={handleClosePaymentModal}
                            className="px-8 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                          >
                            Close
                          </button>
                          <button
                            onClick={() => {
                              handleClosePaymentModal();
                              navigate('/profile'); // Assuming there's a profile/orders page
                            }}
                            className="px-8 py-3 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700 transition-colors shadow-lg"
                          >
                            Track Order
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HomePage;